import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import useLogger from '../../../hooks/useLogger';
import { isMobile } from '../../../utils/device';
import { EventTypes } from '../../../utils/events';
import useStoryComposer from '../hooks/useStoryComposer';
import DoneStep, { StoryOutcome } from './DoneStep';
import GetAppStep from './GetAppStep';
import MakeStep from './MakeStep';
import PhoneHandoff from './PhoneHandoff';
import { FlowContainer, getFlowColors } from './styles';

const Step = {
  MAKE: 'make',
  DONE: 'done',
  APP: 'app',
};

const EntryPoint = {
  CHECKOUT: 'checkout',
  QR: 'qr',
};

/**
 * Task 1 of a challenge: the client adds a photo, picks a filter and a frame, and gets a commitment sticker to
 * share or save. Every way out (sharing, saving or skipping) ends on the app download.
 *
 * Stories are posted from a phone, so a computer only shows a QR code that opens the flow there, and skipping
 * it ends on the app download too.
 */
const CommitmentStickerFlow = ({
  card,
  entryPoint,
  addedCoaching,
  appFooter,
}) => {
  const [step, setStep] = useState(Step.MAKE);
  const [outcome, setOutcome] = useState(null);
  const [hasMadeSticker, setHasMadeSticker] = useState(false);
  const flowRef = useRef(null);
  const composer = useStoryComposer(card);
  const { logEvent } = useLogger();
  // A phone or tablet on Chrome's "Desktop site" sends a computer's user agent, and its code would open this same
  // page, so a touch-first screen counts as a phone too.
  const isPhone = isMobile || !!window.matchMedia?.('(pointer: coarse)').matches;

  useEffect(() => {
    logEvent(EventTypes.STORY_SHARE_VIEWED, {
      challengeId: card.challengeId,
      coachId: card.coachId,
      device: isPhone ? 'phone' : 'computer',
      entryPoint,
      addedCoaching,
    });
  }, [card, entryPoint, addedCoaching, isPhone, logEvent]);

  const style = { filter: composer.filterId, frame: composer.frameId };

  const changeStyle = (change) => {
    logEvent(EventTypes.STORY_STYLE_SELECTED, { ...style, ...change });
    if (change.filter) {
      composer.setFilterId(change.filter);
    }
    if (change.frame) {
      composer.setFrameId(change.frame);
    }
  };

  const finish = (result) => {
    setOutcome(result);
    setStep(Step.APP);
  };

  const skip = (fromStep) => {
    logEvent(EventTypes.STORY_SHARE_SKIPPED, { step: fromStep });
    finish(StoryOutcome.SKIPPED);
  };

  if (!isPhone && step !== Step.APP) {
    return (
      <PhoneHandoff
        card={card}
        stickerUrl={composer.stickerUrl}
        onSkip={() => skip('handoff')}
        footer={appFooter}
      />
    );
  }

  return (
    <FlowContainer ref={flowRef} style={getFlowColors(card)} aria-label={card.challengeName}>
      {step === Step.MAKE && (
        <MakeStep
          challengeName={card.challengeName}
          stickerUrl={composer.stickerUrl}
          hasPhoto={composer.hasPhoto}
          isStoryReady={composer.isStoryReady}
          filterId={composer.filterId}
          frameId={composer.frameId}
          filterPreviews={composer.filterPreviews}
          framePreviews={composer.framePreviews}
          onPhoto={composer.addPhoto}
          onFilterChange={(filter) => changeStyle({ filter })}
          onFrameChange={(frame) => changeStyle({ frame })}
          onCreate={() => {
            setHasMadeSticker(true);
            setStep(Step.DONE);
          }}
          onSkip={() => skip(Step.MAKE)}
          sheetContainer={flowRef}
        />
      )}
      {step === Step.DONE && (
        <DoneStep
          stickerUrl={composer.stickerUrl}
          storyFile={composer.storyFile}
          storyUrl={composer.storyUrl}
          onShareStarted={(method) => logEvent(EventTypes.STORY_SHARED, { ...style, method })}
          onFinish={finish}
          onSkip={() => skip(Step.DONE)}
        />
      )}
      {step === Step.APP && (
        <GetAppStep
          card={card}
          outcome={outcome}
          stickerUrl={hasMadeSticker ? composer.stickerUrl : null}
          footer={appFooter}
        />
      )}
    </FlowContainer>
  );
};

CommitmentStickerFlow.propTypes = {
  card: PropTypes.shape({
    challengeId: PropTypes.string.isRequired,
    coachId: PropTypes.string.isRequired,
    coachName: PropTypes.string.isRequired,
    challengeName: PropTypes.string.isRequired,
    brandColor: PropTypes.string.isRequired,
    isDarkTheme: PropTypes.bool.isRequired,
  }).isRequired,
  // Where the client came from: the checkout, or the QR code a computer showed them.
  entryPoint: PropTypes.oneOf(Object.values(EntryPoint)).isRequired,
  /*
    Whether the client took the coach's 1:1 at the checkout that led here, logged with the flow being shown. Null
    when not known, as on the page the QR code opens on another device.
  */
  addedCoaching: PropTypes.bool,
  // What the page adds under the app download's store buttons, and on a computer under the code too, such as the
  // email the account is linked to.
  appFooter: PropTypes.node,
};

CommitmentStickerFlow.defaultProps = {
  addedCoaching: null,
  appFooter: null,
};

export { EntryPoint, StoryOutcome };
export default CommitmentStickerFlow;
